import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { ArrowUp, ArrowDown, Newspaper, Info, Clock, ArrowsClockwise, CheckCircle, XCircle, WarningCircle, Question } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { label: "1W", value: "5d", interval: "1h" },
  { label: "1M", value: "1mo", interval: "1d" },
  { label: "3M", value: "3mo", interval: "1d" },
  { label: "6M", value: "6mo", interval: "1d" },
  { label: "1Y", value: "1y", interval: "1d" },
];

const VERDICT_STYLES = {
  compliant: "bg-green-50 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800",
  borderline: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800",
  non_compliant: "bg-red-50 text-red-700 border-red-300 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800",
  unverified: "bg-zinc-100 text-zinc-600 border-zinc-300 dark:bg-zinc-800/30 dark:text-zinc-400 dark:border-zinc-600",
};
const VERDICT_LABELS = { compliant: "COMPLIANT", borderline: "BORDERLINE", non_compliant: "NON-COMPLIANT", unverified: "UNVERIFIED" };

function StatusIcon({ status }) {
  if (status === "pass") return <CheckCircle size={16} weight="fill" className="text-green-600 dark:text-green-400 shrink-0" />;
  if (status === "borderline") return <WarningCircle size={16} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0" />;
  if (status === "unavailable") return <Question size={16} weight="fill" className="text-zinc-400 dark:text-zinc-500 shrink-0" />;
  return <XCircle size={16} weight="fill" className="text-red-600 dark:text-red-400 shrink-0" />;
}

function fmt(n) {
  if (!n && n !== 0) return "N/A";
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return n.toLocaleString();
}

function ChartTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-3 py-2.5 text-xs font-mono">
      <p className="text-zinc-400 dark:text-zinc-500 mb-1">{d.date}</p>
      <p className="text-zinc-950 dark:text-zinc-100 font-medium text-sm">${d.close?.toFixed(2)}</p>
      <p className="text-zinc-400 dark:text-zinc-500 mt-0.5">H ${d.high?.toFixed(2)} / L ${d.low?.toFixed(2)}</p>
    </div>
  );
}

function formatUpdatedDate(iso) {
  if (!iso) return "N/A";
  const d = new Date(iso);
  const month = d.toLocaleString("en-US", { month: "long" });
  const year = d.getFullYear();
  return `${month} ${year} (based on latest available financial data)`;
}

export default function StockDetail({ stock, open, onClose, onOpenArticle }) {
  const [chartData, setChartData] = useState([]);
  const [period, setPeriod] = useState("1mo");
  const [quote, setQuote] = useState(null);
  const [news, setNews] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [stockData, setStockData] = useState(null);
  const intervalRef = useRef(null);

  const fetchChart = useCallback(async (p) => {
    if (!stock) return;
    setChartLoading(true);
    const pObj = PERIODS.find((x) => x.value === p) || PERIODS[1];
    try {
      const res = await axios.get(`${API}/stocks/${stock.ticker}/chart`, {
        params: { period: pObj.value, interval: pObj.interval },
      });
      setChartData(res.data.data || []);
    } catch { setChartData([]); } finally { setChartLoading(false); }
  }, [stock]);

  const fetchQuote = useCallback(async () => {
    if (!stock) return;
    try {
      const res = await axios.get(`${API}/stocks/${stock.ticker}/quote`);
      setQuote(res.data);
    } catch { /* */ }
  }, [stock]);

  const fetchNews = useCallback(async () => {
    if (!stock) return;
    try {
      const res = await axios.get(`${API}/stocks/${stock.ticker}/news`);
      setNews(res.data.news || []);
    } catch { /* */ }
  }, [stock]);

  useEffect(() => {
    if (stock && open) {
      setStockData(stock);
      fetchChart(period); fetchQuote(); fetchNews();
      intervalRef.current = setInterval(fetchQuote, 30000);
      return () => clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [stock, open, period, fetchChart, fetchQuote, fetchNews]);

  const handleRefreshFMP = async () => {
    if (!stock || refreshing) return;
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/stocks/lookup/${stock.ticker}`);
      if (res.data) setStockData(res.data);
    } catch { /* */ }
    finally { setRefreshing(false); }
  };

  const activeStock = stockData || stock;
  if (!activeStock) return null;

  const isUp = quote ? quote.change >= 0 : true;
  const color = isUp ? "#0ea5e9" : "#f97316";

  const stats = quote ? [
    { k: "Open", v: `$${quote.open?.toFixed(2)}` },
    { k: "Volume", v: quote.volume ? fmt(quote.volume) : "N/A" },
    { k: "Day High", v: `$${quote.day_high?.toFixed(2)}` },
    { k: "Day Low", v: `$${quote.day_low?.toFixed(2)}` },
    { k: "50D Avg", v: `$${quote.fifty_day_avg?.toFixed(2)}` },
    { k: "200D Avg", v: `$${quote.two_hundred_day_avg?.toFixed(2)}` },
  ] : [];

  const ratios = [
    { label: "Debt / Market Cap", val: activeStock.debt_ratio, st: activeStock.debt_status, threshold: 33, unit: "%" },
    { label: "Interest / Revenue", val: activeStock.interest_ratio, st: activeStock.interest_status, threshold: 5, unit: "%" },
    { label: "Receivables / Assets", val: activeStock.receivable_ratio, st: activeStock.receivable_status, threshold: 49, unit: "%" },
  ];

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[620px] overflow-y-auto rounded-none border-l border-zinc-200 dark:border-zinc-800 p-0 shadow-none bg-white dark:bg-zinc-950"
        data-testid="stock-detail-sheet"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{activeStock.ticker}</SheetTitle>
          <SheetDescription>Details for {activeStock.company_name}</SheetDescription>
        </SheetHeader>

        {/* ---- Header / Compliance Status ---- */}
        <div className="px-4 sm:px-6 pt-6 sm:pt-8 pb-5 border-b border-zinc-100 dark:border-zinc-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500">{activeStock.sector}</p>
              <h2 className="text-lg sm:text-xl tracking-tight font-medium text-zinc-950 dark:text-zinc-100 mt-1 truncate" data-testid="detail-title">
                <span className="font-mono mr-2 text-zinc-500 dark:text-zinc-400">{activeStock.ticker}</span>
                {activeStock.company_name}
              </h2>
            </div>
            <span
              className={`px-2.5 sm:px-3 py-1 text-[10px] font-mono font-bold uppercase border tracking-widest shrink-0 mt-1 ${VERDICT_STYLES[activeStock.verdict] || VERDICT_STYLES.non_compliant}`}
              data-testid="detail-verdict"
            >
              {VERDICT_LABELS[activeStock.verdict] || "N/A"}
            </span>
          </div>

          {quote ? (
            <div className="flex items-baseline gap-3 mt-3 flex-wrap" data-testid="detail-price">
              <span className="text-2xl sm:text-3xl font-light font-mono text-zinc-950 dark:text-zinc-100">${quote.price?.toFixed(2)}</span>
              <span className={`text-sm font-mono font-medium flex items-center gap-1 ${isUp ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                {isUp ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                {isUp ? "+" : ""}{quote.change?.toFixed(2)} ({isUp ? "+" : ""}{quote.change_percent?.toFixed(2)}%)
              </span>
            </div>
          ) : (
            <div className="mt-3 h-9 flex items-center">
              <span className="text-xs font-mono text-zinc-300 dark:text-zinc-600 animate-pulse">Loading quote...</span>
            </div>
          )}

          {/* Last Updated & Refresh */}
          <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
            <div className="flex items-center gap-1.5" data-testid="last-updated">
              <Clock size={11} className="text-zinc-400 dark:text-zinc-500 shrink-0" />
              <span className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500">
                Last Updated: {formatUpdatedDate(activeStock.last_updated)}
              </span>
            </div>
            <button
              onClick={handleRefreshFMP}
              disabled={refreshing}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors disabled:opacity-40 shrink-0"
              data-testid="refresh-fmp-btn"
            >
              <ArrowsClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Updating..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* ---- AAOIFI Financial Ratio Breakdown ---- */}
        <div className="px-4 sm:px-6 py-5 border-b border-zinc-100 dark:border-zinc-800" data-testid="compliance-section">
          <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-4">
            AAOIFI Financial Ratio Breakdown
          </h3>
          <div className="space-y-0">
            {ratios.map((r) => (
              <div key={r.label} className="py-3 border-b border-zinc-50 dark:border-zinc-800/50">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusIcon status={r.st} />
                    <span className="text-sm text-zinc-700 dark:text-zinc-300">{r.label}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {r.st === "unavailable" ? (
                      <span className="text-xs font-mono text-zinc-400 dark:text-zinc-500 italic">Data Unavailable</span>
                    ) : (
                      <span className="text-sm font-mono font-medium text-zinc-950 dark:text-zinc-100 tabular-nums">{r.val}%</span>
                    )}
                  </div>
                </div>
                {r.st !== "unavailable" && (
                  <div className="ml-6 mt-1.5 flex items-center gap-3">
                    <div className="flex-1 h-1.5 bg-zinc-100 dark:bg-zinc-800 relative overflow-hidden">
                      <div
                        className={`absolute left-0 top-0 h-full transition-all ${
                          r.st === "pass" ? "bg-green-500" : r.st === "borderline" ? "bg-amber-500" : "bg-red-500"
                        }`}
                        style={{ width: `${Math.min((r.val / r.threshold) * 100, 100)}%` }}
                      />
                      <div
                        className="absolute top-0 w-px h-full bg-zinc-400 dark:bg-zinc-500"
                        style={{ left: "100%" }}
                      />
                    </div>
                    <span className="text-[10px] font-mono text-zinc-400 dark:text-zinc-500 whitespace-nowrap tabular-nums">
                      Threshold: {r.threshold}%
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between py-3 border-b border-zinc-50 dark:border-zinc-800/50">
            <div className="flex items-center gap-2">
              <StatusIcon status={activeStock.business_status} />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Business Activity</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-mono text-zinc-950 dark:text-zinc-100">
                {activeStock.business_status === "pass" ? "Permissible" : "Prohibited"}
              </span>
              {activeStock.prohibition_reason && (
                <p className="text-[10px] font-mono text-red-500 dark:text-red-400 mt-0.5">{activeStock.prohibition_reason}</p>
              )}
            </div>
          </div>
        </div>

        {/* ---- What This Means For You ---- */}
        <div className="px-4 sm:px-6 py-5 border-b border-zinc-100 dark:border-zinc-800" data-testid="what-this-means-section">
          <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-3 flex items-center gap-1.5">
            <Info size={12} weight="bold" />
            What This Means For You
          </h3>
          <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
            {activeStock.verdict === "compliant"
              ? `${activeStock.company_name || activeStock.ticker} meets AAOIFI criteria and is currently considered halal for investment. All financial ratios are within acceptable thresholds and the business activity is permissible.`
              : activeStock.verdict === "borderline"
                ? `${activeStock.company_name || activeStock.ticker} is near the compliance thresholds. While not currently non-compliant, one or more ratios are close to the limit. Monitor this stock closely as financial changes could push it into non-compliance.`
                : `${activeStock.company_name || activeStock.ticker} exceeds one or more AAOIFI thresholds and is not currently considered halal for investment. ${
                    activeStock.debt_status === "fail" ? "The debt-to-market-cap ratio exceeds the 33% limit. " : ""
                  }${activeStock.interest_status === "fail" ? "Interest-bearing income exceeds the 5% revenue threshold. " : ""
                  }${activeStock.receivable_status === "fail" ? "Accounts receivable exceed the 49% asset threshold. " : ""
                  }Review your holdings accordingly.`
            }
          </p>
          <p className="text-[10px] font-mono text-zinc-300 dark:text-zinc-600 mt-2 italic">
            This is for informational purposes only and does not constitute financial advice.
          </p>
        </div>

        {/* ---- Why This Classification? ---- */}
        {activeStock.explanation && (
          <div className="px-4 sm:px-6 py-5 border-b border-zinc-100 dark:border-zinc-800" data-testid="explanation-section">
            <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-3 flex items-center gap-1.5">
              <Info size={12} weight="bold" />
              Why This Classification?
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
              {activeStock.explanation}
            </p>
            <p className="text-[10px] font-mono text-zinc-300 dark:text-zinc-600 mt-3">
              Screening based on AAOIFI standards.
            </p>
            <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="data-source-badges">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-800/40 bg-emerald-950/30 text-[9px] font-mono tracking-wide text-emerald-400/80">
                <span className="w-1 h-1 rounded-full bg-emerald-500/60 shrink-0" />
                Ratios: {activeStock.source === "fmp+yfinance" ? "FMP + YF" : "FMP"}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-sky-800/40 bg-sky-950/30 text-[9px] font-mono tracking-wide text-sky-400/80">
                <span className="w-1 h-1 rounded-full bg-sky-500/60 shrink-0" />
                Price & News: Yahoo Finance
              </span>
              {activeStock.last_updated && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-zinc-700/40 bg-zinc-800/30 text-[9px] font-mono tracking-wide text-zinc-500" data-testid="data-freshness-badge">
                  <span className="w-1 h-1 rounded-full bg-zinc-500/60 shrink-0" />
                  Updated: {(() => {
                    const d = new Date(activeStock.last_updated);
                    const now = new Date();
                    const hrs = Math.floor((now - d) / 3600000);
                    if (hrs < 1) return "Just now";
                    if (hrs < 24) return `${hrs}h ago`;
                    return `${Math.floor(hrs / 24)}d ago`;
                  })()}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ---- Chart ---- */}
        <div className="px-4 sm:px-6 pt-4" data-testid="period-selector">
          <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-3">Price Chart</h3>
          <div className="flex gap-px flex-wrap">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`period-${p.label}`}
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                  period === p.value
                    ? "bg-zinc-950 dark:bg-zinc-100 text-white dark:text-zinc-950 border-zinc-950 dark:border-zinc-100"
                    : "bg-white dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700 hover:text-zinc-950 dark:hover:text-zinc-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 sm:px-6 py-4" data-testid="stock-chart">
          {chartLoading ? (
            <div className="h-[200px] sm:h-[220px] flex items-center justify-center">
              <div className="inline-block w-5 h-5 border-2 border-zinc-200 dark:border-zinc-700 border-t-zinc-950 dark:border-t-zinc-100 rounded-full animate-spin" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-[200px] sm:h-[220px] flex items-center justify-center">
              <p className="text-xs font-mono text-zinc-300 dark:text-zinc-600">No chart data</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.08} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#fafafa" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "#a1a1aa" }} stroke="transparent" tickLine={false} axisLine={{ stroke: "#f4f4f5" }} interval="preserveStartEnd" minTickGap={50} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "#a1a1aa" }} stroke="transparent" tickLine={false} axisLine={false} width={50} tickFormatter={(v) => `$${v}`} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="close" stroke={color} fill="url(#cg)" strokeWidth={1.5} dot={false} activeDot={{ r: 3, stroke: color, fill: "#fff", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Stats */}
        {quote && (
          <div className="px-4 sm:px-6 py-4 border-t border-zinc-100 dark:border-zinc-800" data-testid="quote-stats">
            <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8">
              {stats.map((s) => (
                <div key={s.k} className="flex justify-between py-2 border-b border-zinc-50 dark:border-zinc-800/50">
                  <span className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">{s.k}</span>
                  <span className="text-sm font-mono text-zinc-950 dark:text-zinc-100">{s.v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* News */}
        <div className="px-4 sm:px-6 py-5 border-t border-zinc-100 dark:border-zinc-800" data-testid="stock-news-section">
          <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-4 flex items-center gap-2">
            <Newspaper size={12} weight="bold" />
            Latest News
          </h3>
          {news.length === 0 ? (
            <p className="text-xs font-mono text-zinc-300 dark:text-zinc-600 py-2">No news available.</p>
          ) : (
            <div className="space-y-0">
              {news.map((a, i) => (
                <button
                  key={i}
                  onClick={() => a.link && onOpenArticle(a.link)}
                  className="w-full text-left py-3 border-b border-zinc-50 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors -mx-2 px-2 group"
                  data-testid={`news-article-${i}`}
                >
                  <p className="text-sm text-zinc-800 dark:text-zinc-300 line-clamp-2 group-hover:text-zinc-950 dark:group-hover:text-zinc-100">{a.title}</p>
                  <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 mt-1">{a.publisher}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="h-6" />
      </SheetContent>
    </Sheet>
  );
}
